import React, {useEffect, useMemo, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch, RootState} from "../../../../store";
import {fetchPlayerStatistics, statisticsId} from "../../../../store/actions/statisticActions";
import {IDType} from "../../../../config/types";
import {PlayerStatisticsListV2, StatisticKeyEnum} from "../../../../client/api";

type LeagueStatisticsPlayerProps = {
  leagueId: IDType;
  onDataLoaded?: (data: PlayerStatisticsListV2) => void;
  isPremiumUser?: boolean;
};

const LeagueStatisticsPlayer: React.FC<LeagueStatisticsPlayerProps> = ({
  leagueId,
  onDataLoaded,
  isPremiumUser,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const tried = useRef(false);
  const lastPremiumUser = useRef<boolean | undefined>(undefined);
  
  const playerStats = useSelector((state: RootState) => {
    const key = statisticsId(leagueId);
    return state.statistics.playerStats[key];
  });
  
  const loading = useSelector((state: RootState) => state.statistics.status === "loading");

  const needsRefetch = useMemo(() => {
    if (!isPremiumUser || !playerStats?.response) return false;
    
    const ratingStat = playerStats.response.find(
      (s) => s.statistic.key === StatisticKeyEnum.PLAYER_RATING
    );
    const assistsStat = playerStats.response.find(
      (s) => s.statistic.key === StatisticKeyEnum.PLAYER_MOST_ASSISTS
    );

    const isRatingEmpty = ratingStat && ratingStat.playerStatistics.length === 0;
    const isAssistsEmpty = assistsStat && assistsStat.playerStatistics.length === 0;

    return isRatingEmpty || isAssistsEmpty;
  }, [isPremiumUser, playerStats]);

  useEffect(() => {
    const shouldRefetch = 
      (lastPremiumUser.current === false && isPremiumUser === true) ||
      (isPremiumUser === true && needsRefetch);
    
    if (shouldRefetch) {
      tried.current = false;
      if (leagueId && !loading) {
        fetchPlayerStatistics(dispatch, leagueId);
      }
    }
    lastPremiumUser.current = isPremiumUser;
  }, [isPremiumUser, leagueId, loading, dispatch, playerStats, needsRefetch]);

  useEffect(() => {
    if (leagueId && !playerStats && !tried.current && !loading) {
      tried.current = true;
      fetchPlayerStatistics(dispatch, leagueId);
    }
  }, [leagueId, playerStats, loading, dispatch, isPremiumUser]);

  useEffect(() => {
    if (playerStats && onDataLoaded) {
      onDataLoaded(playerStats.response);
    }
  }, [playerStats, onDataLoaded, isPremiumUser]);

  return null;
};

export default LeagueStatisticsPlayer;

